import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X as XIcon, AlertTriangle, Loader2, Undo2, ArrowRight } from "lucide-react";

/**
 * RevertConversionModal
 *
 * Admin safety flow to undo an accidental convert-to-franchisee. Two
 * phases:
 *  1. Preflight  — GET /admin/franchisees/{id}/revert-preflight
 *                  Renders linked-activity counts + the resolved
 *                  disposition (hard_delete vs archive) + the pipeline
 *                  stage we'll restore the contact to.
 *  2. Confirm    — the admin types REVERT to unlock the destructive
 *                  action; POST /admin/franchisees/{id}/revert-conversion
 *                  runs the reversal and echoes the outcome as a toast.
 *
 * Kept intentionally lo-fi — this is a rare, admin-only recovery
 * action, not a headline flow.
 */
export default function RevertConversionModal({ franchisee, onClose, onReverted }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pre, setPre] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(
          `/admin/franchisees/${franchisee.id}/revert-preflight`
        );
        if (!cancelled) setPre(data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
              "Could not load the revert preflight. See console for details."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [franchisee.id]);

  const doRevert = async () => {
    if (confirmText.trim().toUpperCase() !== "REVERT") return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(
        `/admin/franchisees/${franchisee.id}/revert-conversion`,
        { confirm: "REVERT" }
      );
      setResult(data);
      if (onReverted) onReverted(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Revert failed — see console for details."
      );
    } finally {
      setBusy(false);
    }
  };

  const counts = pre?.preflight?.counts || {};
  const blocking = pre?.preflight?.blocking || {};
  const disposition = pre?.preflight?.disposition;
  const sectorsToRelease = counts.sectors_to_release || 0;
  const stage = pre?.restore?.target_stage;
  const stageReason = pre?.restore?.reason;

  return (
    <div
      className="fixed inset-0 z-[60] bg-stone-950/60 flex items-center justify-center p-4"
      data-testid="revert-conversion-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-red-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <h2 className="font-display text-base text-red-800">
              Revert to Contact
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="revert-conversion-close"
            className="text-stone-500 hover:text-stone-900"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {loading && (
            <div
              className="flex items-center gap-2 text-sm text-stone-500 py-6 justify-center"
              data-testid="revert-conversion-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin" /> Running preflight…
            </div>
          )}

          {!loading && error && !pre && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
              data-testid="revert-conversion-error"
            >
              {error}
            </div>
          )}

          {!loading && pre && !result && (
            <>
              <p className="text-sm text-stone-700 leading-relaxed">
                This will remove <b>{pre.franchisee.name || "this franchisee"}</b>{" "}
                from the Franchisees list and restore the original contact back
                into your Sales Pipeline.
              </p>

              <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-sm space-y-2">
                <div className="flex items-center gap-2 text-stone-700">
                  <span className="font-bold uppercase tracking-widest text-[10px] text-stone-500">
                    Contact will be restored to
                  </span>
                </div>
                <div
                  className="flex items-center gap-2 text-stone-950"
                  data-testid="revert-conversion-target-stage"
                >
                  <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-xs uppercase font-bold tracking-wider">
                    {stage || "interested"}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-stone-400" />
                  <span className="text-stone-700">
                    {pre.contact.name} ({pre.contact.email || "no email"})
                  </span>
                </div>
                <div className="text-xs text-stone-500 italic">
                  Stage derived: {stageReason}
                </div>
              </div>

              <div className="rounded-xl border border-stone-200 p-3 text-sm space-y-2">
                <div className="font-bold uppercase tracking-widest text-[10px] text-stone-500">
                  Franchisee record — what we found linked
                </div>
                <ul
                  className="space-y-1 text-stone-800"
                  data-testid="revert-conversion-counts"
                >
                  {Object.keys(counts).length === 0 && (
                    <li className="text-stone-500 italic">Nothing to report.</li>
                  )}
                  {Object.entries(counts)
                    .filter(([key]) => key !== "sectors_to_release")
                    .map(([key, val]) => (
                    <li
                      key={key}
                      className={
                        "flex items-center justify-between " +
                        (blocking[key] ? "text-red-700" : "text-stone-600")
                      }
                    >
                      <span className="capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="tabular-nums font-bold">{val}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-2 mt-2 border-t border-stone-200 text-sm">
                  <span className="font-bold text-stone-500 uppercase tracking-widest text-[10px]">
                    Disposition:{" "}
                  </span>
                  {disposition === "hard_delete" ? (
                    <span
                      className="text-red-700 font-bold"
                      data-testid="revert-conversion-disposition"
                    >
                      HARD DELETE — no linked activity, the franchisee record
                      will be removed entirely.
                    </span>
                  ) : (
                    <span
                      className="text-amber-700 font-bold"
                      data-testid="revert-conversion-disposition"
                    >
                      ARCHIVE — linked activity found; the franchisee record
                      will be soft-deleted (hidden from the Franchisees list)
                      rather than removed.
                    </span>
                  )}
                </div>
              </div>

              {sectorsToRelease > 0 && (
                <div
                  className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
                  data-testid="revert-conversion-sectors-to-release"
                >
                  This will return <b>{sectorsToRelease}</b>{" "}
                  {sectorsToRelease === 1 ? "sector" : "sectors"} to the available pool.
                </div>
              )}

              <ul className="text-xs text-stone-500 space-y-1 list-disc pl-5">
                <li>The contact goes back into your Sales Pipeline at the stage above.</li>
                <li>
                  Any linked territory plan is unlinked so it&apos;s available in
                  Territory Builder again.
                </li>
                <li>The conversion audit row is removed from territory history.</li>
                {disposition === "hard_delete" ? (
                  <li>
                    The franchisee record is deleted; auto-created folder stubs
                    are cleared.
                  </li>
                ) : (
                  <li>
                    The franchisee record is archived (tagged “Reverted from
                    franchisee”) and hidden from the Franchisees list.
                  </li>
                )}
              </ul>

              <div className="pt-2 border-t border-stone-200">
                <label
                  className="text-xs uppercase tracking-widest font-bold text-stone-500 mb-1 block"
                  htmlFor="revert-confirm-input"
                >
                  Type REVERT to confirm
                </label>
                <input
                  id="revert-confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  data-testid="revert-conversion-confirm-input"
                  placeholder="REVERT"
                  className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white font-mono tracking-wider focus:outline-none focus:border-red-500"
                />
                {error && (
                  <div className="text-xs text-red-700 mt-2">{error}</div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={onClose}
                  data-testid="revert-conversion-cancel"
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={doRevert}
                  disabled={
                    busy ||
                    confirmText.trim().toUpperCase() !== "REVERT"
                  }
                  data-testid="revert-conversion-execute"
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Undo2 className="w-3.5 h-3.5" />
                  )}
                  {busy ? "Reverting…" : "Revert to Contact"}
                </button>
              </div>
            </>
          )}

          {result && (
            <div
              className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-2"
              data-testid="revert-conversion-result"
            >
              <div className="font-bold">Reverted successfully.</div>
              <div>
                Contact restored to stage{" "}
                <span className="uppercase font-bold">
                  {result.restored_stage}
                </span>
                . Franchisee record: <b>{result.disposition}</b>.
              </div>
              {result.sectors_released > 0 && (
                <div data-testid="revert-conversion-sectors-released">
                  <b>{result.sectors_released}</b>{" "}
                  {result.sectors_released === 1 ? "sector" : "sectors"} returned to the available pool.
                </div>
              )}
              {result.unlinked_plan_id && (
                <div>Territory plan unlinked (id: {result.unlinked_plan_id}).</div>
              )}
              {result.errors && result.errors.length > 0 && (
                <div className="text-amber-700">
                  Some clean-up steps reported issues:{" "}
                  {result.errors.join("; ")}
                </div>
              )}
              <div className="pt-2">
                <button
                  onClick={onClose}
                  data-testid="revert-conversion-done"
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
